import {
  Bell,
  Search,
  Sun,
  Menu,
  Moon,
  Plane,
  LogOut,
  User,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Bookmark,
  Wallet,
} from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CONFIG from "../../config";
import { authActions } from "../../store";
import { getInitials } from "../../utils/function";
import { Person } from "react-bootstrap-icons";

const AdminHeader = () => {
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  let auth = useSelector((x) => x.auth.value);
  let authData = JSON.parse(localStorage.getItem(CONFIG.login_data)) || {};
  let roleId = Number(authData?.user?.role_id);

  const hideMobileNavRoutes = [
    "/points-wallet",
    "/portfolio-summary",
    "/profile",
    "/profile-point-wallet",
    "/deal-saved",
    "/card-select",
    "/results",
    "/deals-details",
  ];

  const shouldHideStore = hideMobileNavRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleProfileClick = (e) => {
    e.stopPropagation();
    if (window.innerWidth < 768) {
      setIsMobileMenuOpen(true);
    } else {
      setDropdownOpen(!dropdownOpen);
    }
  };

  const handleUpdateProfile = () => {
    navigate("/profile");
    setDropdownOpen(false);
    if (window.innerWidth < 768) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleUpdatePointWallet = () => {
    navigate("/profile-point-wallet");
    setDropdownOpen(false);
    if (window.innerWidth < 768) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleSavedDeal = () => {
    navigate("/deal-saved");
    setDropdownOpen(false);
    if (window.innerWidth < 768) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    setIsMobileMenuOpen(false);
    dispatch(authActions.logout());
    localStorage.removeItem(CONFIG.login_data);
    localStorage.removeItem("auth");
    sessionStorage.clear();
    document.documentElement.classList.remove("dark");
    if (roleId === Number(CONFIG.ROLE_ADMIN)) {
      navigate("/secure/login", { replace: true });
      setShowLogoutModal(false);
    } else {
      navigate("/login", { replace: true });
      setShowLogoutModal(false);
    }
  };

  const getTitle = () => {
    const path = location.pathname;

    if (path.includes("/profile-point-wallet")) {
      return "Point Wallet";
    }

    if (path.includes("/profile")) {
      return "Profile";
    }
    if (path.includes("/deal-saved")) {
      return "Saved Deals";
    }
    if (path.includes("/card-select")) {
      return "Select Card";
    }

    if (path.includes("/results")) {
      return "Flights List";
    }
    if (path.includes("/deals-details")) {
      return "Back to search results";
    }

    return "JustForPoints";
  };

  const handleBackClick = () => {
    if (location.pathname.startsWith("/profile")) {
      navigate("/dashboard-screen");
      return;
    }

    if (location.pathname.startsWith("/profile-point-wallet")) {
      navigate("/dashboard-screen");
      return;
    }

    if (location.pathname.startsWith("/deal-saved")) {
      navigate("/dashboard-screen");
      return;
    }

    if (location.pathname.startsWith("/card-select")) {
      navigate("/search");
      return;
    }

    if (location.pathname.startsWith("/results")) {
      navigate(-1);
      return;
    }

    if (location.pathname.startsWith("/deals-details")) {
      navigate(-1);
      return;
    }

    navigate("/dashboard");
  };

  const handleShowLogoutModel = () => {
    setShowLogoutModal(true);

    if (window.innerWidth < 768) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header className="h-16 md:h-20 bg-white/85 dark:bg-[#0A1221]/85 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30 px-4 md:px-8 flex items-center justify-between transition-colors duration-300">
        {/* LEFT LOGO */}

        <div className="flex items-center w-full">
          {shouldHideStore && !location.pathname.includes("/points-wallet") ? (
            <div className="flex items-center gap-3.5 h-16 w-full md:hidden animate-in fade-in slide-in-from-left-2 duration-200">
              <button
                onClick={handleBackClick}
                className="flex items-center justify-center w-9 h-9 rounded-xl 
        bg-slate-50 dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/80
        text-slate-700 dark:text-slate-300 shadow-2xs
        hover:bg-slate-100 dark:hover:bg-slate-800 
        active:scale-95 transition-all duration-200"
                aria-label="Go back"
              >
                <ChevronLeft size={18} className="stroke-[2.5]" />
              </button>

              <span className="text-slate-900 dark:text-white font-black text-base md:text-lg tracking-tight select-none mt-0.5">
                {getTitle() || "Point Wallet"}
              </span>
            </div>
          ) : (
            <div
              onClick={() => window.location.reload()}
              className="md:hidden flex items-center gap-2.5 h-16 select-none cursor-pointer group active:scale-[0.98] transition-transform"
            >
              <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center border border-amber-100 dark:border-amber-500/20 shadow-2xs group-hover:bg-amber-100 transition-colors">
                <Plane className="rotate-45 text-[#D4AF37]" size={18} />
              </div>

              <span className="text-base tracking-tight font-black bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                JustForPoints
              </span>
            </div>
          )}
        </div>

        <div
          className="flex items-center gap-2"
          style={{ marginRight: "10px" }}
        >
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="
            hidden md:flex items-center justify-center p-2.5 
            bg-slate-50 text-slate-600 border border-slate-200/60 shadow-2xs
            hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300
            dark:bg-[#151F32] dark:text-slate-400 dark:border-slate-800/80
            dark:hover:bg-[#1d2b44] dark:hover:text-white dark:hover:border-slate-700
            rounded-xl transition-all duration-200 cursor-pointer 
            active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
          >
            {darkMode ? (
              <Sun
                size={18}
                className="transition-transform duration-300 hover:rotate-45"
              />
            ) : (
              <Moon
                size={18}
                className="transition-transform duration-300 hover:-rotate-12"
              />
            )}
          </button>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <div
            ref={dropdownRef}
            className="relative border-l pl-3 md:pl-4 border-slate-200 dark:border-slate-800"
          >
            <button
              className="flex items-center gap-2 p-1.5 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 md:border-transparent transition-all group"
              onClick={handleProfileClick}
            >
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-[#D4AF37] flex items-center justify-center text-white font-black text-sm">
                {getInitials(authData?.user?.full_name) || "DP"}
              </div>
              <ChevronDown
                size={16}
                className="hidden md:block text-slate-400"
              />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 rounded-2xl shadow-xl py-2 z-50">
                <button
                  onClick={handleUpdateProfile}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                >
                  <User size={15} /> <span>Profile</span>
                </button>

                {/* <button
                  onClick={handleUpdatePointWallet}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                >
                  <Wallet size={15} /> <span>Point Wallet</span>
                </button> */}

                {/* <button
                  onClick={handleSavedDeal}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                >
                  <Bookmark size={15} /> <span> Saved Deals</span>
                </button> */}
                <hr className="border-slate-100 dark:border-slate-800 my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-black text-red-500 hover:bg-red-50"
                >
                  <LogOut size={15} /> <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden bg-white dark:bg-[#0F172A] flex flex-col animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100 dark:border-slate-800/60 bg-white dark:bg-[#0F172A]">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm font-bold tracking-tight py-2"
            >
              <ChevronLeft size={25} />
            </button>
            <h2 className="font-black text-base text-slate-900 dark:text-white tracking-tight">
              Settings
            </h2>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-slate-400 rounded-full bg-slate-50 dark:bg-slate-800"
            ></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6 flex flex-col justify-between">
            <div className="space-y-6">
              <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-100 dark:border-slate-800/60 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center text-white font-black text-base shadow-inner">
                  {getInitials(authData?.user?.full_name) || "J"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-black text-slate-900 dark:text-white truncate">
                    {authData?.user?.full_name || "User"}
                  </p>
                  <p className="text-[10px] font-extrabold text-[#D4AF37] uppercase tracking-widest mt-0.5">
                    {Number(authData?.user?.role_id) === 2
                      ? " Rewards Explorer"
                      : "Superadmin"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">
                  Account Management
                </h3>

                <div className="bg-white dark:bg-slate-900/40 rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800/50">
                  <button
                    className="w-full flex items-center justify-between p-4 bg-white dark:bg-[#1E293B]/10 active:bg-slate-50 border-b border-slate-100 dark:border-slate-800/40"
                    onClick={handleUpdateProfile}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <Person size={18} />
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Profile
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-slate-300" />
                  </button>
                  {/* 
                  <button
                    className="w-full flex items-center justify-between p-4 bg-white dark:bg-[#1E293B]/10 active:bg-slate-50 border-b border-slate-100 dark:border-slate-800/40"
                    onClick={handleUpdatePointWallet}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <Wallet size={18} />
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Points Wallet
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-slate-300" />
                  </button> */}

                  <button
                    className="w-full flex items-center justify-between p-4 bg-white dark:bg-[#1E293B]/10 active:bg-slate-50"
                    onClick={toggleTheme}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-yellow-400 flex items-center justify-center">
                        {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        {darkMode ? "Light Mode" : "Dark Mode"}
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-slate-300" />
                  </button>

                  {/* <button
                    className="w-full flex items-center justify-between p-4 bg-white dark:bg-[#1E293B]/10 active:bg-slate-50 border-b border-slate-100 dark:border-slate-800/40"
                    onClick={handleSavedDeal}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <Bookmark size={18} />
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Saved Deals
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-slate-300" />
                  </button> */}
                </div>
              </div>
            </div>

            {/* HIGH-VISIBILITY LOGOUT ACTION AT BOTTOM */}
            <div className="pt-6 border-t border-slate-100 dark:border-slate-800/60">
              <button
                type="submit"
                onClick={handleShowLogoutModel}
                className="w-full h-12 rounded-xl bg-red-500/10 hover:bg-red-500/20 active:scale-[0.99] text-red-500 flex items-center justify-center gap-2 text-sm font-extrabold tracking-wide transition-all shadow-xs"
              >
                <LogOut size={16} className="stroke-[2.5]" />
                <span>Log Out of Account</span>
              </button>
            </div>

            {showLogoutModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="bg-white dark:bg-[#161e2d] rounded-3xl p-6 w-[90%] max-w-sm shadow-2xl animate-fade-in">
                  <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                    Confirm Logout?
                  </h2>

                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    You will be logged out of your account and need to login
                    again.
                  </p>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowLogoutModal(false)}
                      className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 transition"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={(e) => {
                        setShowLogoutModal(false);
                        handleLogout(e);
                      }}
                      className="px-4 py-2 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition"
                    >
                      Yes, Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminHeader;
